/**
 * Offset-aware datetimes for the journey API.
 *
 * Step 6 accepts **only** ISO 8601 with an explicit UTC offset and rejects a
 * bare local time with a 400. The reason is Portugal: the country is `+01:00`
 * in summer and `+00:00` in winter, so `2026-09-15T07:05:00` denotes two
 * different instants six months apart and neither of them reliably.
 *
 * So the offset is never hardcoded and never taken from the browser's own time
 * zone: a user in another country planning a journey in Lousada still means
 * Lousada's clock. `Intl` is asked which offset applies to the chosen date,
 * which is the only correct answer and stays correct through every future
 * transition without this file changing.
 */

export const LISBON_TIME_ZONE = 'Europe/Lisbon';

const OFFSET_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: LISBON_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZoneName: 'longOffset',
});

function partsOf(instant: Date): Map<string, string> {
  return new Map(OFFSET_FORMATTER.formatToParts(instant).map((part) => [part.type, part.value]));
}

/** `GMT+01:00` → `+01:00`; bare `GMT` (zero offset) → `+00:00`. */
function offsetOf(instant: Date): string {
  const zone = partsOf(instant).get('timeZoneName') ?? 'GMT';
  return zone === 'GMT' ? '+00:00' : zone.replace('GMT', '');
}

/**
 * The Lisbon offset in effect for a given wall-clock date and time.
 *
 * Found by probing the instant rather than computed from a rule: an offset
 * table in application code is a table that goes stale.
 *
 * Ambiguous and non-existent local times (the hour that repeats in October,
 * the hour that does not exist in March) resolve to whatever the probe lands
 * on. That is acceptable for a journey planner: both readings are within an
 * hour of the user's intent, and the alternative is asking a traveller which
 * side of a clock change they meant.
 */
export function lisbonOffsetFor(date: string, time: string): string {
  // Probe as if the wall clock were UTC, then correct once. One iteration is
  // enough because the correction can only move the instant by the offset,
  // which is at most an hour away from a transition.
  const naive = new Date(`${date}T${time}:00Z`);
  if (Number.isNaN(naive.getTime())) return '+00:00';

  const firstGuess = offsetOf(naive);
  const corrected = new Date(`${date}T${time}:00${firstGuess}`);
  return Number.isNaN(corrected.getTime()) ? firstGuess : offsetOf(corrected);
}

/**
 * Builds the `dateTime` the journey API expects.
 *
 * @param date `YYYY-MM-DD`, as an `<input type="date">` produces
 * @param time `HH:mm`, as an `<input type="time">` produces
 */
export function toOffsetIso(date: string, time: string): string {
  return `${date}T${time}:00${lisbonOffsetFor(date, time)}`;
}

/** The current date and time in Lisbon, split for the two inputs. */
export function nowInLisbon(instant: Date = new Date()): { date: string; time: string } {
  const parts = partsOf(instant);
  return {
    date: `${parts.get('year') ?? ''}-${parts.get('month') ?? ''}-${parts.get('day') ?? ''}`,
    time: `${parts.get('hour') ?? ''}:${parts.get('minute') ?? ''}`,
  };
}

/** `2026-09-15T12:16:01+01:00` → `12:16`, in Lisbon terms. */
export function formatClock(isoWithOffset: string): string {
  const instant = new Date(isoWithOffset);
  if (Number.isNaN(instant.getTime())) return '--:--';
  const parts = partsOf(instant);
  return `${parts.get('hour') ?? '--'}:${parts.get('minute') ?? '--'}`;
}

/** Whole minutes, rounded, for display. */
export function formatMinutes(seconds: number): string {
  return `${String(Math.round(seconds / 60))} min`;
}
